/**
 * The icon set.
 *
 * Drawn here rather than pulled from a library. The audit found seventeen
 * different Unicode characters standing in for icons — arrows, a pencil, and one
 * emoji wastebasket — at whatever weight the system font happened to render
 * them. These share a 24px box, a 1.6 stroke, round caps and `currentColor`, so
 * a row of them looks like one family.
 *
 * Musical notation is the exception: a clef is a glyph with a correct shape, not
 * an icon to redraw, so `Clef` uses the engraving character deliberately.
 */

export type IconName =
  | 'home'
  | 'book'
  | 'classroom'
  | 'check'
  | 'close'
  | 'eye'
  | 'eyeOff'
  | 'alert'
  | 'info'
  | 'undo'
  | 'redo'
  | 'trash'
  | 'duplicate'
  | 'plus'
  | 'minus'
  | 'arrowUp'
  | 'arrowDown'
  | 'arrowLeft'
  | 'arrowRight'
  | 'menu'
  | 'pen'
  | 'eraser'
  | 'lasso'
  | 'snap'
  | 'play'
  | 'stop'
  | 'download'
  | 'upload';

const PATHS: Record<IconName, React.ReactNode> = {
  home: (
    <>
      <path d="M3.5 11.2 12 4l8.5 7.2" />
      <path d="M5.8 9.8V20h12.4V9.8" />
      <path d="M9.5 20v-6h5v6" />
    </>
  ),
  book: (
    <>
      <path d="M4 5.5A3.5 3.5 0 0 1 7.5 4H11v15H7.5A3.5 3.5 0 0 0 4 20.5Z" />
      <path d="M20 5.5A3.5 3.5 0 0 0 16.5 4H13v15h3.5a3.5 3.5 0 0 1 3.5 1.5Z" />
    </>
  ),
  classroom: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19c.4-3.2 2.2-5 5.5-5s5.1 1.8 5.5 5" />
      <circle cx="17" cy="9" r="2.2" />
      <path d="M15.7 14.2c2.9-.3 4.6 1.2 4.8 3.8" />
    </>
  ),
  check: <polyline points="4 12.5 9.5 18 20 6.5" />,
  close: (
    <>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3.8-6.5 10-6.5S22 12 22 12s-3.8 6.5-10 6.5S2 12 2 12Z" />
      <circle cx="12" cy="12" r="2.75" />
    </>
  ),
  eyeOff: (
    <>
      <path d="M4.2 8.1C2.9 9.6 2 12 2 12s3.8 6.5 10 6.5c1.6 0 3-.3 4.2-.9" />
      <path d="M9.8 5.7A11 11 0 0 1 12 5.5c6.2 0 10 6.5 10 6.5a17 17 0 0 1-3.2 3.9" />
      <line x1="3" y1="3" x2="21" y2="21" />
    </>
  ),
  alert: (
    <>
      <path d="M12 4.5 21 19.5H3L12 4.5Z" />
      <line x1="12" y1="10" x2="12" y2="14" />
      <circle cx="12" cy="16.8" r="0.9" fill="currentColor" stroke="none" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <line x1="12" y1="11" x2="12" y2="16.5" />
      <circle cx="12" cy="7.8" r="0.9" fill="currentColor" stroke="none" />
    </>
  ),
  undo: (
    <>
      <path d="M4 9h10a5 5 0 0 1 0 10H9" />
      <polyline points="7.5 5.5 4 9 7.5 12.5" />
    </>
  ),
  redo: (
    <>
      <path d="M20 9H10a5 5 0 0 0 0 10h5" />
      <polyline points="16.5 5.5 20 9 16.5 12.5" />
    </>
  ),
  trash: (
    <>
      <path d="M5 7h14" />
      <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7" />
      <path d="M6.5 7l.8 11.2A1.8 1.8 0 0 0 9.1 20h5.8a1.8 1.8 0 0 0 1.8-1.8L17.5 7" />
    </>
  ),
  duplicate: (
    <>
      <rect x="8.5" y="8.5" width="11" height="11" rx="2" />
      <path d="M15.5 5.5h-9a2 2 0 0 0-2 2v9" />
    </>
  ),
  plus: (
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </>
  ),
  minus: <line x1="5" y1="12" x2="19" y2="12" />,
  arrowUp: (
    <>
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="6 11 12 5 18 11" />
    </>
  ),
  arrowDown: (
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <polyline points="6 13 12 19 18 13" />
    </>
  ),
  arrowLeft: (
    <>
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="11 6 5 12 11 18" />
    </>
  ),
  arrowRight: (
    <>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="13 6 19 12 13 18" />
    </>
  ),
  menu: (
    <>
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </>
  ),
  pen: (
    <>
      <path d="M4 20l1.2-4.2L15.4 5.6a2 2 0 0 1 2.8 0l.2.2a2 2 0 0 1 0 2.8L8.2 18.8 4 20Z" />
      <line x1="14.2" y1="6.8" x2="17.2" y2="9.8" />
    </>
  ),
  eraser: (
    <>
      <path d="M8.5 19H20" />
      <path d="M15.5 5.2 5.2 15.5a2 2 0 0 0 0 2.8l1.5 1.5h4l9.1-9.1a2 2 0 0 0 0-2.8l-1.5-1.5a2 2 0 0 0-2.8 0Z" />
    </>
  ),
  lasso: (
    <>
      <ellipse cx="12" cy="10" rx="8" ry="5.5" />
      <path d="M7.5 14.6c-.7 1.4-.4 2.9.9 3.6" />
      <circle cx="9.4" cy="19" r="1.4" />
    </>
  ),
  snap: (
    <>
      <line x1="4" y1="8" x2="20" y2="8" />
      <line x1="4" y1="16" x2="20" y2="16" />
      <polyline points="9 11 12 14 15 11" />
    </>
  ),
  play: <path d="M8 5.5v13l11-6.5-11-6.5Z" fill="currentColor" stroke="none" />,
  stop: <rect x="6.5" y="6.5" width="11" height="11" rx="1.5" />,
  download: (
    <>
      <line x1="12" y1="4" x2="12" y2="15" />
      <polyline points="7 10.5 12 15.5 17 10.5" />
      <path d="M4.5 19.5h15" />
    </>
  ),
  upload: (
    <>
      <line x1="12" y1="15.5" x2="12" y2="4.5" />
      <polyline points="7 9.5 12 4.5 17 9.5" />
      <path d="M4.5 19.5h15" />
    </>
  ),
};

export interface IconProps {
  name: IconName;
  size?: number;
  /**
   * Icons are decorative by default: the control around them carries the
   * accessible name. Pass a title only when the icon is the sole content and
   * nothing else names it.
   */
  title?: string;
  className?: string;
}

export function Icon({ name, size = 20, title, className }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}

export const ICON_NAMES = Object.keys(PATHS) as IconName[];
